import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {Object} PlanPrice
 * @property {string} price
 * @property {string} [compare]
 */

/**
 * @typedef {Object} VariantPrices
 * @property {string} oneTime
 * @property {Record<string, PlanPrice>} plans
 */

/**
 * Purchase option cards (one-time vs subscribe) that keep `selling_plan` in sync
 * with the product form and update displayed prices when the variant changes.
 *
 * @extends {Component}
 */
class PurchaseOptionsComponent extends Component {
  /** @type {Record<string, VariantPrices> | null} */
  #prices = null;

  connectedCallback() {
    super.connectedCallback();
    this.#parsePrices();
    this.#disableAppWidget();
    this.addEventListener('change', this.#onChange);
    this.#sync();

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card, .shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card, .shopify-section');
    target?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * The Shopify Subscriptions app block still outputs a hidden `selling_plan`
   * input. Disable it so it cannot override this picker.
   */
  #disableAppWidget() {
    const root = this.closest('.product-form-buttons, product-form-component, form');
    const appBlock = root?.querySelector('.shopify-app-block[id*="subscriptions"]');
    if (!(appBlock instanceof HTMLElement)) return;

    appBlock.setAttribute('inert', '');
    appBlock.querySelectorAll('input, select, textarea').forEach((el) => {
      if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
        el.disabled = true;
        if (el.name === 'selling_plan') el.removeAttribute('name');
      }
    });
  }

  #parsePrices() {
    const script = this.querySelector('script[type="application/json"]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return;

    try {
      this.#prices = JSON.parse(script.textContent);
    } catch {
      this.#prices = null;
    }
  }

  #onChange = () => {
    this.#sync();
    this.#refreshPrices();
  };

  #sync() {
    const options = this.querySelectorAll('.purchase-options__option');

    options.forEach((option) => {
      if (!(option instanceof HTMLElement)) return;

      const radio = option.querySelector('input[type="radio"]');
      const select = option.querySelector('select');
      const checked = radio instanceof HTMLInputElement && radio.checked;

      option.classList.toggle('is-selected', checked);

      if (!(select instanceof HTMLSelectElement)) return;

      select.disabled = !checked;

      if (checked && select.dataset.sellingPlanInput === 'true') {
        select.name = 'selling_plan';
      } else {
        select.removeAttribute('name');
      }
    });
  }

  /**
   * @param {Event} event
   */
  #handleProductSelect = (event) => {
    const productSelectEvent = /** @type {import('@shopify/events').ProductSelectEvent} */ (event);

    productSelectEvent.promise
      .then(({ variant, detail }) => {
        const variantId = detail?.resource?.id ?? variant?.id;
        if (variantId == null) return;

        this.dataset.variantId = String(variantId);
        this.#refreshPrices();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[purchase-options] Event promise rejected:', error);
      });
  };

  #refreshPrices() {
    const variantId = this.dataset.variantId;
    if (!variantId || !this.#prices) return;

    const data = this.#prices[variantId];
    if (!data) return;

    const oneTime = this.querySelector('[data-price-role="one-time"]');
    if (oneTime) oneTime.textContent = data.oneTime;

    this.querySelectorAll('.purchase-options__option[data-plan-id]').forEach((option) => {
      if (!(option instanceof HTMLElement)) return;

      const select = option.querySelector('select');
      const planId =
        select instanceof HTMLSelectElement && select.value ? select.value : option.dataset.planId;
      if (!planId) return;

      const plan = data.plans?.[planId];
      if (!plan) return;

      option.dataset.planId = planId;

      const compareEl = option.querySelector('[data-price-role="compare"]');
      const priceEl = option.querySelector('[data-price-role="current"]');

      if (compareEl instanceof HTMLElement) {
        if (plan.compare && plan.compare !== plan.price) {
          compareEl.textContent = plan.compare;
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      if (priceEl) priceEl.textContent = plan.price;
    });
  }
}

if (!customElements.get('purchase-options-component')) {
  customElements.define('purchase-options-component', PurchaseOptionsComponent);
}
